/**
 * Snapshot provenance presentation for Campaign Notes / Campaign Strategy.
 *
 * The Campaign Insights page shows live KPI cards directly above narrative that
 * was generated earlier. Without an explicit marker the layout implies the prose
 * describes the numbers beside it — which is how a saved narrative citing 1,580
 * clicks came to sit under a KPI card reading 1,759 clicks and read as current.
 *
 * The fix is structural, not a disclaimer: each narrative states the snapshot it
 * was generated from, and the page says plainly when newer KPI data exists.
 * Nothing here rewrites narrative — staleness is reported, never reconciled.
 *
 * TWIN-PINNED: byte-identical in client (Cortex) and superadmin (Quantum).
 * Any change must land in both repos in the same pair of PRs.
 */

export const PROVENANCE_STATE = Object.freeze({
  CURRENT: "current",
  STALE: "stale",
  UNKNOWN: "unknown",
});

/** `Aug 12, 2026 · 9:01 AM` — the governed provenance timestamp format. */
export function formatProvenanceTimestamp(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const day = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${day} · ${time}`;
}

/**
 * The label a narrative panel carries.
 *
 * A version with no bound snapshot resolves to `unknown` and says so, rather
 * than implying a binding that was never recorded.
 */
export function buildNarrativeProvenanceLabel(provenance) {
  const stamp = formatProvenanceTimestamp(provenance?.boundSnapshotGeneratedAt);
  if (!provenance?.boundSnapshotId || !stamp) {
    return "Snapshot provenance unavailable for this saved version";
  }
  return `Based on KPI snapshot · ${stamp}`;
}

/** The label above the live KPI cards. */
export function buildCurrentKpiLabel(provenance) {
  const stamp = formatProvenanceTimestamp(provenance?.currentSnapshotGeneratedAt);
  return stamp ? `Updated ${stamp}` : "";
}

/**
 * Whether to surface the `New KPI data available` state.
 *
 * Only a proven difference qualifies. An `unknown` binding is NOT reported as
 * stale — an unrecorded provenance is not evidence that the narrative is out of
 * date, and claiming otherwise would push users to regenerate valid content.
 */
export function isNarrativeStale(provenance) {
  return provenance?.state === PROVENANCE_STATE.STALE;
}

export function buildStaleNotice(provenance) {
  if (!isNarrativeStale(provenance)) return "";
  const changed = Array.isArray(provenance?.changedMetrics) ? provenance.changedMetrics.length : 0;
  return changed > 0
    ? `New KPI data available · ${changed} metric${changed === 1 ? "" : "s"} changed`
    : "New KPI data available";
}

/**
 * The complete presentation contract for one narrative panel.
 * Pure and deterministic so both portals render an identical state.
 */
export function resolveNarrativeProvenanceView(provenance) {
  return {
    state: provenance?.state || PROVENANCE_STATE.UNKNOWN,
    isStale: isNarrativeStale(provenance),
    narrativeLabel: buildNarrativeProvenanceLabel(provenance),
    currentKpiLabel: buildCurrentKpiLabel(provenance),
    staleNotice: buildStaleNotice(provenance),
    changedMetrics: provenance?.changedMetrics || [],
  };
}
